import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { Pager } from '../components/Pager'
import { api, formatVnd, type Product, type ProductSource } from '../lib/api'
import { useQueryState } from './ContentPages'

function ProductMedia({ p, fallback }: { p: Product; fallback?: string | null }) {
  const [failed, setFailed] = useState(false)
  if (!p.image || failed) {
    return fallback ? <img className="bfm-fallback" src={fallback} alt={p.name} loading="lazy" /> : <i className="fa-solid fa-gift" aria-hidden />
  }
  if (p.media === 'video') {
    return (
      <video
        src={p.image}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        aria-label={p.name}
        onError={() => setFailed(true)}
      />
    )
  }
  return <img src={p.image} alt={p.name} loading="lazy" onError={() => setFailed(true)} />
}

type WalletMode = 'xu' | 'gem'
type OrderIntent = { product: Product; mode: WalletMode | 'bank'; note: string }

function useEscape(onClose: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])
}

function SkinPreview({ name }: { name: string }) {
  const [shown, setShown] = useState('')
  const [bad, setBad] = useState(false)
  useEffect(() => {
    const t = window.setTimeout(() => {
      setBad(false)
      setShown(/^[A-Za-z0-9_]{3,16}$/.test(name.trim()) ? name.trim() : '')
    }, 600)
    return () => window.clearTimeout(t)
  }, [name])
  if (!shown || bad) return null
  return (
    <img
      className="bfm-skin"
      src={`https://mc-heads.net/body/${encodeURIComponent(shown)}/100`}
      alt={shown}
      onError={() => setBad(true)}
    />
  )
}

function NameField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="bfm-name">
      <label>
        Tên nhân vật
        <input value={value} onChange={(e) => onChange(e.target.value)} required maxLength={32} autoFocus />
      </label>
      <SkinPreview name={value} />
    </div>
  )
}

/** TT ví xu / ví gem — xác nhận tên nhân vật đã lưu hoặc nhập tên khác (buyitforme/item_detail) */
function WalletOrderModal({ intent, onClose }: { intent: OrderIntent & { mode: WalletMode }; onClose: () => void }) {
  const { user, refresh } = useAuth()
  const nav = useNavigate()
  const { product, mode, note } = intent
  const saved = user?.tennhanvat?.trim() || ''
  const [step, setStep] = useState<'confirm' | 'name'>(saved ? 'confirm' : 'name')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  useEscape(onClose)

  const price = mode === 'gem' ? product.gem_price || 0 : product.price
  const unit = mode === 'gem' ? 'gem' : 'xu'
  const payLabel = mode === 'gem' ? 'Thanh toán ví gem' : 'Đúng rồi, đặt hàng!'

  async function order(characterName: string) {
    setErr('')
    setBusy(true)
    try {
      await api.createBuyOrder({
        product_id: product.id,
        character_name: characterName,
        note: note.trim() || undefined,
        pay_method: mode,
      })
      await refresh()
      nav('/account/dat-ho', {
        state: {
          flash:
            mode === 'gem'
              ? `Đặt hàng thành công! Đã trừ ${price.toLocaleString('vi-VN')} gem. Chờ shop xử lí đơn hàng cho bạn nhé!`
              : 'Đặt hàng thành công! Chờ shop xử lí đơn hàng cho bạn nhé!',
        },
      })
    } catch (ex) {
      setErr((ex as Error).message)
      setBusy(false)
    }
  }

  function submitName(e: FormEvent) {
    e.preventDefault()
    void order(name.trim())
  }

  const lowBalance = /nạp/i.test(err)

  return (
    <div className="amc-modal-backdrop" onClick={onClose}>
      <div className="amc-modal" role="dialog" aria-modal="true" aria-label="Đặt hàng" onClick={(e) => e.stopPropagation()}>
        <h2>{product.name}</h2>
        <p className="bfm-charge">
          <i className={mode === 'gem' ? 'fa-regular fa-gem' : 'fa-solid fa-coins'} aria-hidden /> {price.toLocaleString('vi-VN')} {unit}
        </p>
        {step === 'confirm' ? (
          <>
            <p className="bfm-confirm">
              Đặt cho nhân vật <strong>{saved}</strong>
            </p>
            <SkinPreview name={saved} />
            {err ? (
              <p className="note">
                {err} {lowBalance ? <Link to={mode === 'gem' ? '/deposit' : '/deposit?tab=card'}>Nạp thêm</Link> : null}
              </p>
            ) : null}
            <div className="amc-modal-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setStep('name')} disabled={busy}>
                Tên nhân vật khác
              </button>
              <button type="button" className="btn btn-primary" onClick={() => order(saved)} disabled={busy}>
                {busy ? 'Đang đặt…' : payLabel}
              </button>
            </div>
          </>
        ) : (
          <form className="form-stack" onSubmit={submitName}>
            <NameField value={name} onChange={setName} />
            {err ? (
              <p className="note">
                {err} {lowBalance ? <Link to={mode === 'gem' ? '/deposit' : '/deposit?tab=card'}>Nạp thêm</Link> : null}
              </p>
            ) : null}
            <div className="amc-modal-actions">
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                Hủy
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy || name.trim().length < 3}>
                {busy ? 'Đang đặt…' : mode === 'gem' ? 'TT ví gem' : 'Đặt hàng'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

/** Chuyển khoản — nhập tên nhân vật → hóa đơn (buyitforme.chuyen-khoan) */
function BankOrderModal({ product, onClose }: { product: Product; onClose: () => void }) {
  const { user, refresh } = useAuth()
  const nav = useNavigate()
  const [name, setName] = useState(user?.tennhanvat || '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  useEscape(onClose)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setErr('')
    setBusy(true)
    try {
      const r = await api.createBuyOrder({ product_id: product.id, character_name: name.trim(), pay_method: 'bank' })
      await refresh()
      nav(`/checkout/${r.order.id}`, {
        state: { flash: 'Đã tạo hóa đơn. Thanh toán xong, shop sẽ giao RANK / ITEM cho bạn.' },
      })
    } catch (ex) {
      setErr((ex as Error).message)
      setBusy(false)
    }
  }

  return (
    <div className="amc-modal-backdrop" onClick={onClose}>
      <div className="amc-modal" role="dialog" aria-modal="true" aria-label="Nhập tên nhân vật" onClick={(e) => e.stopPropagation()}>
        <h2>Nhập tên nhân vật</h2>
        <p className="bfm-charge">
          {product.name} · {formatVnd(product.price_transfer)}
        </p>
        <form className="form-stack" onSubmit={submit}>
          <NameField value={name} onChange={setName} />
          {err ? <p className="note">{err}</p> : null}
          <div className="amc-modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Đóng
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy || !name.trim()}>
              {busy ? 'Đang tạo…' : 'Gửi'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function BuyCard({
  p,
  fallback,
  onOrder,
}: {
  p: Product
  fallback?: string | null
  onOrder: (intent: OrderIntent) => void
}) {
  const { user } = useAuth()
  const [note, setNote] = useState('')
  const [noteErr, setNoteErr] = useState(false)

  function pick(mode: OrderIntent['mode']) {
    if (mode !== 'bank' && p.required_note && !note.trim()) {
      setNoteErr(true)
      return
    }
    onOrder({ product: p, mode, note })
  }

  return (
    <article className="bfm-card">
      <div className="bfm-media">
        <ProductMedia p={p} fallback={fallback} />
        {p.group ? <span className="bfm-tag">{p.group}</span> : null}
      </div>
      <div className="bfm-body">
        <h3 title={p.name}>{p.name}</h3>
        {p.required_text ? <p className="bfm-req">Lưu ý: {p.required_text}</p> : null}
        <p className="bfm-price">
          <strong>{formatVnd(p.price_transfer)}</strong>
          {p.old_price ? <s className="muted">{formatVnd(p.old_price)}</s> : null}
        </p>
        <p className="bfm-alt muted">
          {p.gem_price ? (
            <span>
              <i className="fa-regular fa-gem" aria-hidden /> {p.gem_price.toLocaleString('vi-VN')}
            </span>
          ) : null}
          <span className="bfm-xu">
            <i className="fa-solid fa-coins" aria-hidden /> {p.price.toLocaleString('vi-VN')} xu
          </span>
        </p>
        {p.required_note ? (
          <textarea
            className={`bfm-note${noteErr ? ' bfm-note-err' : ''}`}
            rows={2}
            value={note}
            onChange={(e) => {
              setNote(e.target.value)
              setNoteErr(false)
            }}
            maxLength={1000}
            placeholder="Ghi chú"
            aria-label="Ghi chú"
          />
        ) : null}
        <div className={`bfm-actions${p.gem_price ? '' : ' bfm-actions-2'}`}>
          {p.gem_price ? (
            <button
              type="button"
              className="btn btn-sm bfm-btn-gem"
              onClick={() => pick('gem')}
              title={user ? `Ví gem: ${user.balance2.toLocaleString('vi-VN')}` : undefined}
            >
              <i className="fa-regular fa-gem" aria-hidden /> Ví gem
            </button>
          ) : null}
          <button type="button" className="btn btn-primary btn-sm" onClick={() => pick('xu')}>
            <i className="fa-solid fa-coins" aria-hidden /> Ví xu
          </button>
          <button type="button" className="btn btn-sm bfm-btn-bank" onClick={() => pick('bank')}>
            <i className="fa-solid fa-building-columns" aria-hidden /> Chuyển khoản
          </button>
        </div>
      </div>
    </article>
  )
}

/** Tìm ngay khi gõ (trễ 300ms), không cần bấm nút */
function QuickSearch({ value, onSearch }: { value: string; onSearch: (q: string) => void }) {
  const [q, setQ] = useState(value)
  const latest = useRef(onSearch)
  latest.current = onSearch
  useEffect(() => setQ((cur) => (cur.trim() === value ? cur : value)), [value])
  useEffect(() => {
    if (q.trim() === value) return
    const t = window.setTimeout(() => latest.current(q.trim()), 300)
    return () => window.clearTimeout(t)
  }, [q, value])
  return (
    <form
      className="content-search bfm-search"
      role="search"
      onSubmit={(e) => {
        e.preventDefault()
        latest.current(q.trim())
      }}
    >
      <i className="fa-solid fa-magnifying-glass" aria-hidden />
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Tìm vật phẩm, creator…"
        aria-label="Tìm vật phẩm"
      />
      {q ? (
        <button type="button" className="bfm-search-clear" onClick={() => setQ('')} aria-label="Xóa">
          <i className="fa-solid fa-xmark" aria-hidden />
        </button>
      ) : null}
    </form>
  )
}

function SourceList({ sources, err }: { sources: ProductSource[]; err: string }) {
  return (
    <div className="bfm">
      <h1 className="page-title">Đặt hộ vật phẩm</h1>
      <p className="page-lead">Rank, cosmetic, vật phẩm server quốc tế — giao tận nhân vật.</p>
      {err ? <p className="note">{err}</p> : null}
      <div className="bfm-sources">
        {sources
          .filter((s) => s.product_count > 0)
          .map((s) => (
            <Link key={s.id} className="bfm-source" to={`/buyitforme/${s.id}`}>
              <span className="bfm-source-name">{s.name}</span>
              <span className="bfm-source-media">
                {s.image_url ? <img src={s.image_url} alt={s.name} loading="lazy" /> : <i className="fa-solid fa-gift" aria-hidden />}
              </span>
              <span className="bfm-source-count">{s.product_count} sản phẩm</span>
            </Link>
          ))}
      </div>
    </div>
  )
}

export function BuyItForMePage() {
  const { sourceId } = useParams()
  const { user } = useAuth()
  const nav = useNavigate()
  const [params, set] = useQueryState()
  const page = Number(params.get('page')) || 1
  const q = params.get('q') || ''
  const categoryId = params.get('category_id') || ''
  const nhom = params.get('nhom') || ''
  const [sources, setSources] = useState<ProductSource[]>([])
  const [data, setData] = useState<Awaited<ReturnType<typeof api.sourceProducts>> | null>(null)
  const [loading, setLoading] = useState(false)
  const [intent, setIntent] = useState<OrderIntent | null>(null)
  const [err, setErr] = useState('')
  const seq = useRef(0)
  const listTop = useRef<HTMLDivElement>(null)

  useEffect(() => {
    api
      .sources()
      .then((r) => setSources(r.sources))
      .catch((e) => setErr(e.message))
  }, [])

  useEffect(() => {
    if (!sourceId) return
    const id = ++seq.current
    setLoading(true)
    setErr('')
    api
      .sourceProducts(Number(sourceId), { q, category_id: categoryId, nhom, page })
      .then((r) => id === seq.current && setData(r))
      .catch((e) => id === seq.current && setErr(e.message))
      .finally(() => id === seq.current && setLoading(false))
  }, [sourceId, q, categoryId, nhom, page])

  useEffect(() => setData(null), [sourceId])

  if (!sourceId) return <SourceList sources={sources} err={err} />

  const src = sources.find((s) => s.id === Number(sourceId))
  const order = (i: OrderIntent) =>
    user ? setIntent(i) : nav('/login', { state: { from: `/buyitforme/${sourceId}` } })
  const goPage = (p: number) => {
    set({ page: p })
    listTop.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  const allCount = src?.product_count

  return (
    <div className="bfm content-page">
      <p className="muted">
        <Link to="/buyitforme">← Đặt hộ</Link>
      </p>
      <section className="content-hero">
        <div className="bfm-head">
          {src?.image_url ? <img src={src.image_url} alt="" /> : null}
          <div>
            <h1 className="page-title">{src?.name || 'Đặt hộ'}</h1>
            {src?.description ? <p className="page-lead">{src.description}</p> : null}
          </div>
        </div>
      </section>

      <div className="bfm-toolbar" ref={listTop}>
        <QuickSearch value={q} onSearch={(v) => set({ q: v, page: null }, { replace: true })} />
        {categoryId && data && data.groups.length > 1 ? (
          <label className="bfm-group">
            <select value={nhom} onChange={(e) => set({ nhom: e.target.value || null, page: null })} aria-label="Nhóm">
              <option value="">Tất cả nhóm</option>
              {data.groups.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {data?.categories.length ? (
        <div className="rp-versions bfm-cats" aria-label="Danh mục">
          <button
            type="button"
            className={`rp-chip${!categoryId ? ' active' : ''}`}
            onClick={() => set({ category_id: null, nhom: null, page: null })}
          >
            Tất cả{allCount ? <span className="bfm-cat-n">{allCount.toLocaleString('vi-VN')}</span> : null}
          </button>
          {data.categories.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`rp-chip${categoryId === String(c.id) ? ' active' : ''}`}
              onClick={() => set({ category_id: c.id, nhom: null, page: null })}
            >
              {c.name}
              <span className="bfm-cat-n">{c.count.toLocaleString('vi-VN')}</span>
            </button>
          ))}
        </div>
      ) : null}

      {err ? <p className="note">{err}</p> : null}
      {data ? (
        <p className="muted rp-count">
          {data.total.toLocaleString('vi-VN')} vật phẩm
          {data.total_pages > 1 ? ` · Trang ${page}/${data.total_pages}` : ''}
        </p>
      ) : (
        <p className="muted">Đang tải…</p>
      )}
      {data && !data.products.length ? <p className="muted bfm-empty">Không tìm thấy vật phẩm phù hợp.</p> : null}

      <div className={`bfm-grid${loading && data ? ' is-loading' : ''}`}>
        {data?.products.map((p) => (
          <BuyCard key={p.id} p={p} fallback={src?.image_url} onOrder={order} />
        ))}
      </div>
      {data ? <Pager page={page} totalPages={data.total_pages} onPage={goPage} /> : null}
      {intent?.mode === 'bank' ? <BankOrderModal product={intent.product} onClose={() => setIntent(null)} /> : null}
      {intent && intent.mode !== 'bank' ? (
        <WalletOrderModal intent={{ ...intent, mode: intent.mode }} onClose={() => setIntent(null)} />
      ) : null}
    </div>
  )
}
